import type { Branded } from "@@/utilities/branded";
import { fromUnixTime, parse } from "date-fns";
import { fromZonedTime, toDate, toZonedTime } from "date-fns-tz";
import type { Except } from "type-fest";
import { z } from "zod";
import { defaultTimeZone } from "../weekly-schedules/default-time-zone";
import type { FullDateParsed } from "./full-date-parsed";
import type { IsoAndUnixTimestamp, WithUnixTimestamp } from "./iso-and-unix";
import { tz, type Timezone } from "./timezone";

type FullDateString = Branded<number, "FullDateString">;
type FullDate = Branded<number, "FullDate">;

const toFullDateString = (year: number, month: number, day: number) =>
    `${year}${("0" + month).slice(-2)}${("0" + day).slice(-2)}`;

/**
 * Returns yyyymmdd as integer. \
 * E.g. 20210427
 *
 * OBS! Defaults to timezone "Europe/Stockholm"
 *
 * #### NOTE!
 * Remember the date retrieved is in the same timezone \
 * as the client it's run, if timezone hasn't been specified.
 * @param {IsoAndUnixTimestamp} input
 * @returns
 */
function toFullDate(input: Date, timezone?: Timezone): FullDate;
function toFullDate(input: IsoAndUnixTimestamp, timezone?: Timezone): FullDate;
function toFullDate(input: WithUnixTimestamp, timezone?: Timezone): FullDate;
function toFullDate(input: undefined, timezone?: Timezone): undefined;
function toFullDate(
    input: IsoAndUnixTimestamp | undefined,
    timezone?: Timezone,
): FullDate | undefined;
function toFullDate(
    input: Date | IsoAndUnixTimestamp | undefined,
    timezone?: Timezone,
): FullDate | undefined;
function toFullDate(
    input: Except<FullDateParsed, "dayOfWeek">,
    timezone?: Timezone,
): FullDate;
function toFullDate(
    input:
        | Date
        | WithUnixTimestamp
        | IsoAndUnixTimestamp
        | Except<FullDateParsed, "dayOfWeek">
        | undefined,
    timezone = defaultTimeZone,
): FullDate | undefined {
    if (typeof input === "undefined") return undefined;
    const date =
        input instanceof Date
            ? input
            : "unix" in input
              ? fromUnixTime(input.unix)
              : toDate(toFullDateString(input.year, input.month, input.day), {
                    timeZone: timezone,
                });

    const zoned = toZonedTime(date, timezone);

    return parseInt(
        toFullDateString(
            zoned.getFullYear(),
            zoned.getMonth() + 1,
            zoned.getDate(),
        ),
        10,
    ) as FullDate;
}

// TODO: Better verification of full date
// TODO: e.g. make sure it's a year, month and day
const fullDateZodSchema = z.number() as unknown as z.ZodSchema<FullDate>;

//* Converts a full date to a date in the timezone "Europe/Stockholm"
// fromZon
//
//
// */
const fullDateToSwedishTZ = (fullDate: FullDate, timeZone?: Timezone) => {
    const start = parse(fullDate.toString(), "yyyyMMdd", new Date());
    return fromZonedTime(start, timeZone ?? tz.EuropeStockholm);
};

export { fullDateToSwedishTZ, fullDateZodSchema, toFullDate, toFullDateString };
export type { FullDate, FullDateString };
